import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";
import {getCriteriaDeleteErrorMessage} from "@/pages/admin/CriteriaPage/components/criteriaDeleteHelpers";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {
  decrementTotalData,
  getCriteria,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalData,
  setTotalPages,
} from "@redux/slices/criteriaSlices/tableSlice";
import {deleteCriteria} from "@redux/slices/criteriaSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const CriteriaTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);

  const {
    data,
    page,
    size,
    sort,
    search,
    totalPages,
    totalData,
    status,
    selectedData,
  } = useSelector((state) => state.criteria.table);

  const [params, setParams] = useState({
    page: 1,
    size: 5,
    sort,
  });

  const buildParams = useCallback(
    (overrides = {}) => ({
      page,
      size,
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
      ...overrides,
    }),
    [page, size, search, sort],
  );

  const refreshTable = useCallback(
    (overrides = {}) => {
      const newParams = buildParams(overrides);
      setParams(newParams);
      dispatch(getCriteria(newParams));
    },
    [buildParams, dispatch],
  );

  const removeAllCriteria = useCallback(async () => {
    const results = await Promise.all(
      (selectedData ?? []).map((item) =>
        dispatch(deleteCriteria({id: item.id})),
      ),
    );

    const failed = results.find((result) =>
      deleteCriteria.rejected.match(result),
    );

    if (failed) {
      return getCriteriaDeleteErrorMessage(failed.payload, t);
    }

    if (
      results.length > 0 &&
      results.every((result) => deleteCriteria.fulfilled.match(result))
    ) {
      dispatch(setTotalData(totalData - results.length));
      refreshTable();
      closePopup();
    }
  }, [selectedData, totalData, dispatch, refreshTable, closePopup, t]);

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalData / +option)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value.toLowerCase()));
        dispatch(getCriteria({page: 1, size, search: value}));
      }, 500);
    },
    [dispatch, size],
  );

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() => removeAllCriteria()}
      />,
      {closeButton: false},
    );
  };

  const removeCriteria = useCallback(
    async (id) => {
      const resultAction = await dispatch(deleteCriteria({id}));

      if (deleteCriteria.fulfilled.match(resultAction)) {
        dispatch(decrementTotalData());
        refreshTable();
        closePopup();
        return;
      }

      return getCriteriaDeleteErrorMessage(resultAction.payload, t);
    },
    [dispatch, refreshTable, closePopup, t],
  );

  const handleClickDeleteCriteria = (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => removeCriteria(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    {
      id: 1,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={!selectedData || selectedData.length <= 1}
        />
      ),
    },
  ];

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.name"),
        accessor: "name",
        sortable: true,
        width: 700,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        sortable: true,
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 505,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div className={"flex justify-end gap-[8rem] phone:gap-[40rem]"}>
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <>
                <TableButton
                  icon={"edit"}
                  color={"var(--blueLight)"}
                  label={t("table.button.edit")}
                  action={"edit"}
                  item={row.original}
                />
                <TableButton
                  icon={"delete"}
                  color={"var(--red)"}
                  label={t("table.button.delete")}
                  action={"delete"}
                  onClickAction={() => handleClickDeleteCriteria(row.original)}
                  item={row.original}
                />
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (!demo) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, handleClickDeleteCriteria]);

  useEffect(() => {
    const newParams = buildParams({
      ...(page > 0 ? {page} : {page: 1}),
      ...(size > 0 ? {size} : {size: 5}),
    });

    setParams(newParams);
  }, [page, size, search, sort, buildParams]);

  useDeepCompareEffect(() => {
    dispatch(getCriteria(params));
  }, [params]);

  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalData / size)));
  }, [totalData, size, dispatch]);

  useEffect(() => {
    if (page === totalPages + 1) {
      dispatch(setPage(Math.ceil(totalData / size)));
    }
  }, [totalPages, totalData, size, page, dispatch]);

  return (
    <ScreenLayout
      title={t("page.criteria.title")}
      controlsList={!demo && controls}
    >
      <TableLayout
        serverStatus={status}
        columns={columns}
        data={data}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_criteria")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
      />
    </ScreenLayout>
  );
};
